import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import routes from "../../routes";
import NotFound from "../../Views/NotFound/NotFound";
import Sidebar from "../Sidebar";
import { MainNav } from "../MuiTable";
import { useMediaQuery } from "@mui/material";
import { Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { updateCompanyNameFromToken, setPageTitle, getPageTitleFromRoute } from "../../utils/pageTitle";
import {
  CJ_PORTAL_SIDEBAR_COLLAPSED_PX,
  CJ_PORTAL_SIDEBAR_EXPANDED_PX,
} from "../../utils/cjPortalSidebarMetrics";
import {
  CustomerDarkModeProvider,
  useCustomerDarkMode,
} from "../../contexts/CustomerDarkModeContext";
import "./admin-dark-overrides.css";

function ClientInner() {
  const { darkMode } = useCustomerDarkMode();
  const mainContent = useRef(null);
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const [isSidebarDisplay, setIsSidebarDisplay] = useState(true);
  // `noSsr` so the first render reads the real viewport width instead of the
  // SSR default (false) — without this, mobile pages briefly mount with the
  // desktop sidebar marginLeft applied, squeezing content into a ~135px column.
  const isMediumScreen = useMediaQuery("(max-width:767px)", { noSsr: true });

  // Reset sidebar state when screen size changes
  useEffect(() => {
    if (!isMediumScreen) {
      setIsSidebarDisplay(true);
      setIsSidebarClosed(false);
    } else {
      setIsSidebarDisplay(false);
    }
  }, [isMediumScreen]);

  useEffect(() => {
    // JOB-3245: updateCompanyNameFromToken is handled by ProtectedRoute; don't
    // repeat it here. Just set the title synchronously.
    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, []);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }

    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, [location]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("admin-dark-active");
    } else {
      document.body.classList.remove("admin-dark-active");
    }
    return () => document.body.classList.remove("admin-dark-active");
  }, [darkMode]);

  const portalTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          secondary: {
            main: darkMode ? "#f48fb1" : "#dc004e",
          },
          background: {
            default: darkMode ? "#0f1419" : "#f7f8fa",
            paper: darkMode ? "#1a2332" : "#ffffff",
          },
          ...(darkMode
            ? {
                text: {
                  primary: "#e8eaed",
                  secondary: "#9aa0a6",
                },
                divider: "#2d3b4e",
              }
            : {}),
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
        },
        typography: {
          fontFamily: "'Poppins', sans-serif",
          h1: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
          },
          h2: {
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 500,
          },
          body1: {
            fontFamily: "'Poppins', sans-serif",
          },
        },
      }),
    [darkMode]
  );

  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.layout === "/:CompanyUrl/customers" && !prop.isCollapse) {
        return (
          <Route path={prop?.path} element={prop?.component} key={key} exact />
        );
      } else if (prop.children) {
        return prop.children?.map((item) => (
          <Route
            path={item?.path}
            element={item?.component}
            key={key}
            exact
          />
        ));
      }
      return null;
    });
  };

  return (
    <ThemeProvider theme={portalTheme}>
    <Grid
      className={`admin-shell${darkMode ? " admin-shell--dark" : ""}`}
      style={{
        backgroundColor: "var(--app-page-background)",
        width: "100vw",
        height: "100vh",
        overflow: "auto",
        overflowX: "hidden",
      }}
    >
      <Sidebar
        layout={`/:CompanyUrl/customers`}
        isSidebarClosed={isSidebarClosed}
        setIsSidebarClosed={setIsSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
      />

      <MainNav
        layout="/:CompanyUrl/customers"
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
        isSidebarClosed={isSidebarClosed}
      />
      <Grid
        className="main-content"
        ref={mainContent}
        sx={{
          boxSizing: "border-box",
          marginLeft: !isMediumScreen
            ? isSidebarClosed
              ? `${CJ_PORTAL_SIDEBAR_COLLAPSED_PX}px`
              : `${CJ_PORTAL_SIDEBAR_EXPANDED_PX}px`
            : "15px",
          width: !isMediumScreen
            ? `calc(100% - ${
                isSidebarClosed
                  ? CJ_PORTAL_SIDEBAR_COLLAPSED_PX
                  : CJ_PORTAL_SIDEBAR_EXPANDED_PX
              }px)`
            : "calc(100% - 15px)",
          maxWidth: !isMediumScreen
            ? `calc(100% - ${
                isSidebarClosed
                  ? CJ_PORTAL_SIDEBAR_COLLAPSED_PX
                  : CJ_PORTAL_SIDEBAR_EXPANDED_PX
              }px)`
            : "calc(100% - 15px)",
          minWidth: 0,
          px: { xs: 1.5, sm: 2, md: 4 },
          pb: 2,
        }}
      >
        <Grid sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", minWidth: 0 }}>
          <React.Suspense fallback={null}>
            <Routes>
              {getRoutes(routes)}
              {/* JOB-3220 + JOB-3225: real 404 instead of silent /index redirect */}
              <Route path="*" element={<NotFound homePath={`/${CompanyUrl}/customers/index`} />} />
            </Routes>
          </React.Suspense>
        </Grid>
      </Grid>
    </Grid>
    </ThemeProvider>
  );
}

const Client = () => (
  <CustomerDarkModeProvider>
    <ClientInner />
  </CustomerDarkModeProvider>
);

export default Client;
